/**
 * 🧾 국세청 매입계산서 「마지막 업로드」 표시 (v2.239.0 · 사용자 요청 2026-10-13)
 *
 * 「매입관리에서 최근 국세청 자료 업로드한 날짜 · 마지막 업로드된 국세청 자료 시점을 보고 싶다」.
 *   사업자(SM·EJ)마다 한 줄 — 언제 · 누가 올렸나 + 반영된 자료가 **작성일 며칠 것까지**인가.
 *   마우스를 올리면 최근 12건(파일 이름·작성일 범위·장수).
 *
 * 쓰는 곳 두 군데(같은 부품):
 *   · 매입 목록 머리 단추 줄 왼쪽(variant="bar") — ⚠두 줄(11px)이라 단추 높이(32px) 안에 들어간다.
 *     줄을 하나 더 만들면 아래 표 높이(100vh − 320px)를 밀어 표 끝이 화면 밖으로 나간다.
 *   · 업로드 창(variant="box") — 올리기 전에 지금 어디까지 반영돼 있는지 보게.
 *     ⚠「다음 날부터 받으면 된다」고 안내하지 말 것 — 작성일이 지난 날짜로 늦게 발급되는 계산서를 놓친다.
 *
 * ⭐시각은 서버가 만든 한국 시각 글자를 그대로 찍는다(여기서 Date 로 다시 해석하지 않는다).
 * ⚠2026-10-13 이전 업로드는 사업자별 「마지막 업로드한 날」만 되살렸다(국세청 품목 내역 저장 시각) —
 *   올린 사람·장수를 모르는 줄이 있어 「올린 사람 미상」으로 보인다(짐작으로 채우지 않는다).
 */

import { useQuery } from '@tanstack/react-query';
import { getNtsUploadLog, type NtsUploadLog, type NtsUploadLogRow } from '../lib/nts-import-api';

const COMPANIES = ['SM', 'EJ'] as const;

function CoBadge({ id }: { id: 'SM' | 'EJ' }) {
  // 매출매입관리대장 화면(AnalysisCollectionTab)과 같은 색 — SM 회색 · EJ 주황
  return (
    // 폭 고정 — SM·EJ 글자 폭이 달라 두 줄의 시각이 몇 px 어긋나 보였다
    <span className={`inline-block w-[22px] text-center rounded text-[11px] font-bold leading-[14px] ${
      id === 'EJ' ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-700'
    }`}>{id}</span>
  );
}

const who = (r: NtsUploadLogRow) => r.uploadedName || r.uploadedBy || '올린 사람 미상';

/** 마우스 올림 글 — 최근 12건 */
function tooltipOf(log: NtsUploadLog | undefined): string {
  if (!log) return '';
  const lines = log.rows.map((r) => {
    const range = r.minWriteDate && r.maxWriteDate
      ? (r.minWriteDate === r.maxWriteDate ? r.minWriteDate : `${r.minWriteDate}~${r.maxWriteDate}`)
      : '작성일 범위 미상';
    return [
      r.uploadedAt, r.companyId, who(r), `작성일 ${range}`,
      r.rowCount != null ? `${r.rowCount}장` : '',
      r.fileName ?? '',
      r.restored ? '(되살린 기록)' : '',
    ].filter(Boolean).join(' · ');
  });
  return [
    `국세청 매입계산서 업로드 기록 (최근 ${log.rows.length}건)`,
    ...lines,
    '',
    '「작성일 ~날짜」 = 그 사업자로 올린 파일들의 계산서 작성일 중 가장 늦은 날 — 그 날까지의 국세청 자료가 반영되어 있습니다.',
    '※ 2026-10-13 이전 업로드는 사업자별 마지막 업로드한 날만 되살렸습니다(올린 사람·장수를 모르는 줄이 있습니다).',
  ].join('\n');
}

export function NtsLastUpload({ variant = 'bar' }: { variant?: 'bar' | 'box' }) {
  const q = useQuery({
    queryKey: ['purchases', 'nts-upload-log'],
    queryFn: getNtsUploadLog,
    staleTime: 60_000,
  });
  // 못 불러오면 조용히 비운다 — 매입 목록을 막을 일이 아니다(통로 오류는 서버 로그에 남는다)
  if (q.isError) return null;
  const log = q.data;

  const line = (co: 'SM' | 'EJ') => {
    const c = log?.companies[co];
    const last = c?.last ?? null;
    return (
      <div key={co} className="flex items-center gap-1 whitespace-nowrap">
        <CoBadge id={co} />
        {q.isPending ? (
          <span className="text-slate-400">확인 중…</span>
        ) : last ? (
          <>
            <strong className="font-semibold text-slate-700 tabular-nums">{last.uploadedAt}</strong>
            <span>· {who(last)}</span>
            {c?.dataUntil && (
              <span>· 작성일 <strong className="font-semibold text-emerald-700 tabular-nums">~{c.dataUntil}</strong> 까지</span>
            )}
          </>
        ) : (
          <span className="text-slate-400">업로드 기록 없음</span>
        )}
      </div>
    );
  };

  if (variant === 'box') {
    return (
      <div className="border border-emerald-200 bg-emerald-50/60 rounded px-3 py-2 text-xs text-slate-600" title={tooltipOf(log)}>
        <div className="font-semibold text-slate-700 mb-1">🧾 사업자별 마지막 업로드</div>
        <div className="space-y-0.5">{COMPANIES.map(line)}</div>
        <div className="text-xs text-slate-500 mt-1">
          기간을 겹쳐 올려도 이미 처리된 계산서는 「이미 처리됨」으로 넘어갑니다 — 늦게 발급된 계산서를 놓치지 않게 넉넉히 받아 올려도 됩니다.
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-0 text-xs leading-[15px] text-slate-500" title={tooltipOf(log)}>
      <div className="flex items-center gap-1 shrink-0 font-semibold text-slate-600">
        <img src="/nts-logo.webp" alt="" className="h-4 w-auto" />
        <span>국세청 마지막 업로드</span>
      </div>
      <div className="min-w-0">{COMPANIES.map(line)}</div>
    </div>
  );
}
